import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardOutlined, LoadingOutlined } from '@ant-design/icons';
import { getCampusesByIds, getShiftsByIds } from '../../api/user';
import { getSubscriptionV2, type GetSubscriptionV2Output, type SubscriptionV2Summary, calculateTotalAdjustment, getSubscriptionTransactions, collectStampedTxnIds } from '../../api/payment';
import type { Student } from '../../api/user';
import { ROUTES } from '../../utils/constants';
import { AdjustedAmount } from '../subscription/AdjustedAmount';
import { RoutingDisclaimer } from '../ui/RoutingDisclaimer/RoutingDisclaimer';
import { RouteInfoTile } from '../subscription/RouteInfoTile';
import { StudentInviteSection } from './StudentInviteSection';
import './styles/studentcard.css';

interface StudentCardProps {
  student: Student;
  onCompletePayment: (studentId: string, campusData?: any, shiftData?: any) => void;
  onPayInstallment?: (params: {
    studentId: string;
    subscriptionId: string;
    installmentNumber: number;
    amountRupees: number;
    campusData?: any;
    shiftData?: any;
    subscriptionPreview?: any;
    usedTransactionIds?: string[];
  }) => void;
  subscriptionV2Data?: SubscriptionV2Summary;
}

export const StudentCard: React.FC<StudentCardProps> = ({
  student,
  onCompletePayment,
  onPayInstallment,
  subscriptionV2Data,
}) => {
  const navigate = useNavigate();
  const [campusData, setCampusData] = useState<any>(null);
  const [shiftData, setShiftData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<GetSubscriptionV2Output | null>(null);
  const [totalAdjustment, setTotalAdjustment] = useState(0);
  const [usedTransactionIds, setUsedTransactionIds] = useState<string[]>([]);

  const subscriptionId = subscriptionV2Data?.id;

  useEffect(() => {
    const fetchCampusAndShift = async () => {
      setLoading(true);
      try {
        if (student.campusId) {
          const campusResponse = await getCampusesByIds([student.campusId]);
          if (campusResponse?.campuses?.length && campusResponse?.campuses?.length > 0) {
            const campus = campusResponse.campuses[0];
            if (campus.location && typeof campus.location === 'string') {
              const parts = (campus.location as string).trim().split(' ');
              if (parts.length === 2) {
                campus.location = { lat: parseFloat(parts[1]), lng: parseFloat(parts[0]) } as any;
              }
            }
            setCampusData(campus);
          }
        }
        const shiftId = student.shifts?.[0];
        if (shiftId) {
          const shiftResponse = await getShiftsByIds([shiftId]);
          if (shiftResponse.shifts?.length && shiftResponse.shifts?.length > 0) {
            setShiftData(shiftResponse.shifts[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching campus/shift:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampusAndShift();
  }, [student.campusId, student.shifts]);

  useEffect(() => {
    if (subscriptionId) {
      const fetchV2Details = async () => {
        try {
          const details = await getSubscriptionV2(subscriptionId);
          setSubscriptionDetails(details);

          // Fetch PENDING adjustment transactions for this subscription
          const adjustmentTransactions = await getSubscriptionTransactions(subscriptionId);
          const usedIds = collectStampedTxnIds(
            details?.token,
            details?.paymentCalendar?.installments,
          );

          // Collect transaction IDs already stamped on any installment or token —
          // these have been committed to a previous payment and must not be reused.
          const usedTransactionIds = new Set<string>();
          if (details?.token) {
            const tokenIds = [
              (details.token as { transactionId?: string }).transactionId,
              ...((details.token as { transactionIds?: string[] }).transactionIds ?? []),
            ].filter((id): id is string => !!id);
            tokenIds.forEach(id => usedTransactionIds.add(id));
          }
          (details?.paymentCalendar?.installments ?? []).forEach((inst: { transactionId?: string; transactionIds?: string[] }) => {
            if (inst.transactionId) usedTransactionIds.add(inst.transactionId);
            (inst.transactionIds ?? []).forEach(id => usedTransactionIds.add(id));
          });

          // All unused PENDING adjustments land on the first pending installment
          // (CREDIT reduces, DEBIT increases) — same cascade as the admin show page.
          const pendingTxns = adjustmentTransactions.filter(
            (t: { status: string; id: string }) => t.status === 'PENDING' && !usedTransactionIds.has(t.id)
          );
          const adjustment = pendingTxns.length > 0 ? calculateTotalAdjustment(pendingTxns) : 0;
          setTotalAdjustment(adjustment);
          setUsedTransactionIds(usedIds);
        } catch (err) {
          console.warn('Could not fetch V2 subscription details:', err);
        }
      };
      fetchV2Details();
    }
  }, [subscriptionId]);

  const formatGrade = (grade?: string) => {
    if (!grade) return '';
    return grade.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // LAM-150: next pending installment within INSTALLMENT_DUE_DAYS window
  const nextPendingInstallment = (() => {
    if (!subscriptionDetails?.paymentCalendar?.installments) return null;
    return subscriptionDetails.paymentCalendar.installments
      .filter((inst: any) => inst.status === 'PENDING' || inst.status === 'OVERDUE')
      .sort((a: any, b: any) => a.dueDate.localeCompare(b.dueDate))[0] ?? null;
  })();

  const installmentDueBanner = (() => {
    if (!nextPendingInstallment || !subscriptionV2Data?.id) return null;
    if (!nextPendingInstallment.dueDate) return null;
    const dueDate = new Date(nextPendingInstallment.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const isOverdue = daysUntilDue < 0;
    const totalInstallments = subscriptionDetails?.paymentCalendar?.installments?.length ?? 0;
    
    // Calculate adjusted amount: original amount + total adjustments
    const originalAmount = nextPendingInstallment.amount as number;
    const adjustedAmount = originalAmount + totalAdjustment;
    
    return {
      daysUntilDue,
      isOverdue,
      amount: adjustedAmount,
      originalAmount: originalAmount,
      totalAdjustment: totalAdjustment,
      dueDate: nextPendingInstallment.dueDate as string,
      installmentNumber: nextPendingInstallment.installmentNumber as number,
      totalInstallments,
      subscriptionId: subscriptionV2Data.id,
    };
  })();

  const isPaymentInProgress = student.status === 'PAYMENT_IN_PROGRESS';

  // Show complete payment only when: not ACTIVE, not PENDING_ROUTING, not PAYMENT_IN_PROGRESS,
  // and no active subscription exists
  const shouldShowCompletePayment =
    !isPaymentInProgress &&
    !installmentDueBanner &&
    !subscriptionV2Data?.id &&
    student.status != null &&
    ['PENDING_TOKEN_AMOUNT', 'PENDING_DUES', 'INACTIVE', 'PAUSED',
     'TERMINATED', 'IN_COLLECTIONS', 'DELINQUENT'].includes(student.status);

  // Next due row: first pending installment due date + amount
  const nextDueRow = (() => {
    if (!subscriptionDetails?.paymentCalendar?.installments) return null;
    const pending = subscriptionDetails.paymentCalendar.installments.find(
      (inst: any) => inst.status === 'PENDING' || inst.status === 'OVERDUE',
    );
    if (!pending) return null;
    return {
      amount: (pending.amount as number) + totalAdjustment,
      originalAmount: pending.amount as number,
      adj: totalAdjustment,
      dueDate: pending.dueDate as string,
    };
  })();

  // Shift display label
  const shiftLabel = shiftData?.displayLabel || shiftData?.name || null;

  // Status badge config — all statuses from StudentStatus model
  const statusConfig: Record<string, { label: string; cls: string }> = {
    ACTIVE:               { label: 'Active',              cls: 'sc__badge--active' },
    PENDING_ROUTING:      { label: 'Pending Routing',     cls: 'sc__badge--routing' },
    PENDING_TOKEN_AMOUNT: { label: 'Pending Payment',     cls: 'sc__badge--pending' },
    PENDING_DUES:         { label: 'Pending Dues',        cls: 'sc__badge--dues' },
    PAYMENT_IN_PROGRESS:  { label: 'Payment In Progress', cls: 'sc__badge--processing' },
    INACTIVE:             { label: 'Inactive',            cls: 'sc__badge--inactive' },
    PAUSED:               { label: 'Paused',              cls: 'sc__badge--paused' },
    TERMINATED:           { label: 'Terminated',          cls: 'sc__badge--terminated' },
    IN_COLLECTIONS:       { label: 'In Collections',      cls: 'sc__badge--collections' },
    DELINQUENT:           { label: 'Delinquent',          cls: 'sc__badge--delinquent' },
  };
  const statusInfo = statusConfig[student.status ?? ''] ?? { label: student.status ?? '', cls: '' };

  return (
    <div className="sc">
      {/* ── Dark navy header ── */}
      <div className="sc__header">
        <div className="sc__header-top">
          <div>
            <h3 className="sc__name">{student.name || 'Student'}</h3>
            <p className="sc__meta">
              {formatGrade(student.grade)}
              {formatGrade(student.grade) && campusData?.name ? ' · ' : ''}
              {campusData?.name || ''}
            </p>
          </div>
          <span className={`sc__badge ${statusInfo.cls}`}>
            <span className="sc__badge-dot" />
            {statusInfo.label.toUpperCase()}
          </span>
        </div>
      </div>

      {/* ── Key-value rows ── */}
      <div className="sc__body">
        {/* Service dates: start and end */}
        {subscriptionDetails && (
          <div className="sc__dates-row">
            <div className="sc__date-col">
              {subscriptionDetails.startDate && (
                <span className="sc__date-value">
                  {new Date(subscriptionDetails.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              <span className="sc__date-label">Service Start</span>
            </div>
            <div className="sc__date-col sc__date-col--right">
              {subscriptionDetails.shiftDetails?.validUntil && (
                <span className="sc__date-value">
                  {new Date(subscriptionDetails.shiftDetails.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              <span className="sc__date-label">Subscription End</span>
            </div>
          </div>
        )}

        {/* Shift */}
        {(loading || shiftLabel) && (
          <div className="sc__row">
            <span className="sc__row-label">Shift</span>
            <span className="sc__row-value">
              {loading ? '—' : shiftLabel}
            </span>
          </div>
        )}

        {/* Next Due */}
        {nextDueRow && (
          <div className="sc__row">
            <span className="sc__row-label">Next Due</span>
            <div className="sc__row-value sc__row-value--due" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <AdjustedAmount original={nextDueRow.originalAmount} adj={nextDueRow.adj} />
              <span>{new Date(nextDueRow.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Pending Routing disclaimer ── */}
      {student.status === 'PENDING_ROUTING' && (
        <RoutingDisclaimer variant="compact" />
      )}

      {/* ── Route Info Tile ── */}
      {student.id && student.status === 'ACTIVE' && (
        <RouteInfoTile studentId={student.id} />
      )}

      {/* ── Payment In Progress banner — blocks all other alerts ── */}
      {isPaymentInProgress && (
        <div className="sc__banner sc__banner--processing">
          <LoadingOutlined className="sc__banner-icon sc__banner-icon--processing" />
          <div className="sc__banner-body">
            <span className="sc__banner-title sc__banner-title--processing">Payment Processing</span>
            <span className="sc__banner-sub">Your payment is being processed. Please wait 10–15 minutes.</span>
          </div>
        </div>
      )}

      {/* ── Installment due banner — shown when due, suppresses action required ── */}
      {!isPaymentInProgress && installmentDueBanner && (
        <div className={`sc__banner sc__banner--due${installmentDueBanner.isOverdue ? ' sc__banner--overdue' : ''}`}>
          <div className="sc__banner-body">
            <span className={`sc__banner-title${installmentDueBanner.isOverdue ? ' sc__banner-title--overdue' : ' sc__banner-title--due'}`}>
              {installmentDueBanner.isOverdue
                ? `⚠️ Overdue by ${Math.abs(installmentDueBanner.daysUntilDue)} day${Math.abs(installmentDueBanner.daysUntilDue) === 1 ? '' : 's'}`
                : `🔔 Due in ${installmentDueBanner.daysUntilDue} day${installmentDueBanner.daysUntilDue === 1 ? '' : 's'}`}
            </span>
            <span className="sc__banner-sub" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <span>
                Installment {installmentDueBanner.installmentNumber}
                {installmentDueBanner.totalInstallments > 0 ? ` of ${installmentDueBanner.totalInstallments}` : ''}
              </span>
              <AdjustedAmount
                original={installmentDueBanner.originalAmount}
                adj={installmentDueBanner.totalAdjustment}
                layout="inline"
                align="start"
              />
            </span>
          </div>
          <button
            className="sc__pay-btn sc__pay-btn--orange"
            onClick={() =>
              student.id &&
              onPayInstallment?.({
                studentId: student.id,
                subscriptionId: installmentDueBanner.subscriptionId,
                installmentNumber: installmentDueBanner.installmentNumber,
                amountRupees: installmentDueBanner.originalAmount,
                campusData,
                shiftData,
                subscriptionPreview: subscriptionDetails,
                usedTransactionIds,
              })
            }
            disabled={loading}
          >
            <CreditCardOutlined style={{ fontSize: 14 }} /> Pay
          </button>
        </div>
      )}

      {/* ── Action Required + Complete Payment — only when no subscription and no due banner ── */}
      {!isPaymentInProgress && shouldShowCompletePayment && (
        <>
          <div className="sc__banner sc__banner--action">
            <span className="sc__banner-icon">⚠️</span>
            <div className="sc__banner-body">
              <span className="sc__banner-title sc__banner-title--action">Action Required</span>
              <span className="sc__banner-sub">Complete token payment to activate transport service</span>
            </div>
          </div>
          <button
            className="sc__pay-btn sc__pay-btn--dark"
            onClick={() => student.id && onCompletePayment(student.id, campusData, shiftData)}
            disabled={loading || !campusData || !shiftData}
          >
            <CreditCardOutlined style={{ fontSize: 16 }} />
            {loading ? 'Loading...' : 'Complete Payment'}
          </button>
        </>
      )}

      {/* ── View Subscription Details ── */}
      {subscriptionId && (
        <button
          className="sc__view-sub-btn"
          onClick={() => navigate(ROUTES.SUBSCRIPTION_DETAILS, { state: { subscriptionId } })}
        >
          View Subscription Details →
        </button>
      )}

      {/* ── Invite student to the app (ZAP-74) ── */}
      {student.id && <StudentInviteSection studentId={student.id} studentName={student.name} />}
    </div>
  );
};
